import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Flex, SimpleSpinner, Text, VisuallyHidden } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { range } from '~publish/helpers/range'

import type { CommentData } from '../context/CommentContext'

import { CommentCardSkeleton, CommentCard } from '../CommentCard'
import { CommentsError } from '../CommentsError'
import { BackfillLoadingBanner_Channel } from './BackfillLoadingBanner'
import { CommentsEngagementPanel_Channel } from './CommentsEngagementPanel'
import {
  useCommentsData,
  useCommentsEmptyState,
  useCommentListCoordination,
} from './hooks'
import { useScrollToTop } from '../../hooks/useScrollToTop'
import {
  isMonthlyCleanupStatus,
  MonthlyCleanupBanner,
  MonthlyCleanupBanner_Channel,
} from './MonthlyCleanupBanner'
import { NewItemsDivider } from '../../components/NewItemsDivider'
import { useNewEngagementBoundary } from '../../hooks/engagement/useNewEngagementBoundary'
import { useCommunityConfig } from '../../CommunityConfig'
import { CommentContextProvider } from '../context/CommentContext'
import { useCommentCountsForPost } from '../context/CommentsByPostCountsContext'
import {
  EngagementCoordinationProvider,
  useEngagementCoordinationStore,
} from '../../context/EngagementCoordinationContext'
import {
  selectIsExpanded,
  selectIsLocallyResolved,
} from '../../hooks/engagement/engagementCoordinationStore'
import { useChannelMap } from '../../hooks/useChannelMap'
import { EmptyStateAlignedWithFeed } from '../../components/EmptyStates'
import { NoMediaHint, useNoMediaChannels } from '../../components/NoMediaHint'
import { RetentionNotice } from '../../components/RetentionNotice'
import {
  isOlderThanRetentionWindow,
  isRetentionWindowActive,
} from '../../util/retention'
import { useIsByPostView } from '../../util/routeUtils'
import type { CommentStatusWithAll } from '../../types'
import type { BaseCommunityFilters } from '../../CommunityFilters/types'
import type { CommentsPostCard_PostData } from '../../fragments/CommentsPostCard_Post'
import { useFilters } from '../../CommunityFilters/useFilters'

import styles from './CommentsList.module.css'

import type { HTMLAttributes, JSX } from 'react'

export const CommentsList_Channel = graphql(
  `
    fragment CommentsList_Channel on Channel @_unmask {
      id
      ...BackfillLoadingBanner_Channel
      ...CommentsEngagementPanel_Channel
      ...MonthlyCleanupBanner_Channel
    }
  `,
  [
    BackfillLoadingBanner_Channel,
    CommentsEngagementPanel_Channel,
    MonthlyCleanupBanner_Channel,
  ],
)

/**
 * `CommentCard`, `useChannelMap`, and `config.getPermanentNote` still type
 * their channel against the full codegen `Channel`, so channels flowing through
 * this list must satisfy both them and the colocated fragment. The intersection
 * collapses to the fragment type once those consumers migrate (CORE-6384).
 */
export type CommentsListChannel = FragmentOf<typeof CommentsList_Channel> &
  React.ComponentProps<typeof CommentCard>['channel']

type CommentListItemProps = {
  comment: CommentData
  channel: React.ComponentProps<typeof CommentCard>['channel'] | undefined
  isLastItem: boolean
  lastElementRef: ((node: HTMLElement | null) => void) | undefined
  onExpandNextComment: (fromCommentId?: string, onlyRepliable?: boolean) => void
  onExpandPreviousComment: () => void
  onManualExpansion: (commentId: string | null) => void
  onLocalResolutionChange: (commentId: string, isResolved: boolean) => void
  isNew: boolean
}

const CommentListItem = ({
  comment,
  channel,
  isLastItem,
  lastElementRef,
  onExpandNextComment,
  onExpandPreviousComment,
  onManualExpansion,
  onLocalResolutionChange,
  isNew,
}: CommentListItemProps): JSX.Element | null => {
  // Subscribe per-card so a navigation change re-renders only the cards whose
  // expanded / resolved state actually flipped, not the whole list.
  const expanded = useEngagementCoordinationStore(selectIsExpanded(comment.id))
  const isLocallyResolved = useEngagementCoordinationStore(
    selectIsLocallyResolved(comment.id),
  )

  // Skip rendering if channel not found (shouldn't happen in normal operation)
  if (!channel) {
    return null
  }

  return (
    <CommentContextProvider key={comment.id} comment={comment}>
      <CommentCard
        ref={isLastItem ? lastElementRef : undefined}
        channel={channel}
        onExpandNextComment={onExpandNextComment}
        onExpandPreviousComment={onExpandPreviousComment}
        onManualExpansion={onManualExpansion}
        expanded={expanded}
        onLocalResolutionChange={onLocalResolutionChange}
        isLocallyResolved={isLocallyResolved}
        isNew={isNew}
      />
    </CommentContextProvider>
  )
}

type CommentsListProps = {
  channels: CommentsListChannel[]
  onNavigateToUnreplied?: () => void
  isLoading?: boolean
  onBannerDismissed?: () => void
  /** By-post view only. */
  post?: Pick<
    CommentsPostCard_PostData,
    'id' | 'dueAt' | 'externalLink' | 'channelService'
  >
}

const FlexMotion = m.create(Flex)

export const CommentsList = React.memo(function CommentsList(
  props: CommentsListProps,
): JSX.Element {
  return (
    <EngagementCoordinationProvider>
      <CommentsListContent {...props} />
    </EngagementCoordinationProvider>
  )
})

type CommentsEmptyStateViewProps = {
  emptyState: JSX.Element
}

/** The list-empty view: the empty state pinned to the feed's card column. */
const CommentsEmptyStateView = ({
  emptyState,
}: CommentsEmptyStateViewProps): JSX.Element => (
  <EmptyStateAlignedWithFeed>{emptyState}</EmptyStateAlignedWithFeed>
)

type CommentsListNoticesProps = {
  singleChannel: CommentsListChannel | null
  channels: CommentsListChannel[]
  shouldCheckForOldComments: boolean
  onBannerDismissed?: () => void
  showEmptyStateWithComments: boolean
  emptyState: JSX.Element | null
}

/**
 * Notices shown above the feed, in order: the caught-up celebratory empty
 * state (shown above a still-visible list once everything is resolved), the
 * no-media hint, the channel's permanent note, and the resolve-old-comments
 * prompt.
 */
const CommentsListNotices = ({
  singleChannel,
  channels,
  shouldCheckForOldComments,
  onBannerDismissed,
  showEmptyStateWithComments,
  emptyState,
}: CommentsListNoticesProps): JSX.Element => {
  const { config } = useCommunityConfig()
  const permanentNote = singleChannel
    ? config.getPermanentNote(singleChannel)
    : null
  // `CommentsForPost` renders this same list once per post, where a list-level
  // hint would repeat alongside every post the user opens.
  const isByPostView = useIsByPostView()
  const noMediaChannels = useNoMediaChannels(channels, 'comment')

  return (
    <>
      <AnimatePresence>
        {showEmptyStateWithComments && emptyState && (
          <EmptyStateAlignedWithFeed>{emptyState}</EmptyStateAlignedWithFeed>
        )}
      </AnimatePresence>
      {!isByPostView && (
        <NoMediaHint
          noMediaChannels={noMediaChannels}
          engagementType="comment"
        />
      )}
      {permanentNote && (
        <Text size="sm" color="subtle" className={styles.permanentNote}>
          {permanentNote}
        </Text>
      )}
      {/* By-post view renders the prompt itself, above the post card — the feed
          there starts below the post, too low for a prompt about the channel. */}
      {shouldCheckForOldComments && !isByPostView && (
        <MonthlyCleanupBanner
          channels={channels}
          onBannerDismissed={onBannerDismissed}
        />
      )}
    </>
  )
}

function CommentsListContent({
  channels,
  onNavigateToUnreplied,
  isLoading = false,
  onBannerDismissed,
  post,
}: CommentsListProps): JSX.Element {
  const postId = post?.id

  const {
    filters: { status: filterStatus, sortOption, publishedAt },
    utils: { clearFilters, hasActiveFilters },
  } = useFilters()

  const filters = React.useMemo<BaseCommunityFilters>(
    () => ({ status: filterStatus, sortOption, publishedAt, postId }),
    [filterStatus, sortOption, publishedAt, postId],
  )

  // Extract channelIds from channels array
  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  const channelById = useChannelMap(channels)

  const { config } = useCommunityConfig()

  // The empty-state explainer and the permanent note are single-channel only.
  const singleChannel = channels.length === 1 ? channels[0] : null
  const singleChannelId = singleChannel?.id ?? null
  // Realtime channels (webhook/firehose) never backfill, so the explainer copy
  // and gating differ from polling channels.
  const isSingleChannelRealtime = singleChannel
    ? config.isRealtimeSync(singleChannel)
    : false

  const { query, comments } = useCommentsData({
    channelIds,
    filters,
  })

  const { loading, error, fetchingMore, lastElementRef, retry } = query

  const status: CommentStatusWithAll | undefined = filters.status

  const coordination = useCommentListCoordination({
    comments: comments.list,
    channelIds,
    filters,
    isLoading: loading,
  })

  // Scroll to top when all comments are resolved
  const containerRef = useScrollToTop(coordination.allLocallyResolved)

  // New items land at the top on every sort except oldest-first, where they
  // land at the bottom; the boundary orients the divider and scroll to match.
  const direction = sortOption === 'oldest' ? 'bottom' : 'top'
  const commentIds = React.useMemo(
    () => comments.list.map((comment) => comment.id),
    [comments.list],
  )
  const { newlyArrivedIds, dividerIndex } = useNewEngagementBoundary({
    kind: 'comment',
    itemIds: commentIds,
    direction,
    containerRef,
    resetKey: `${channelIds.join(',')}|${filterStatus}|${sortOption}|${publishedAt}|${postId}`,
  })

  // Tells "no unreplied comments" from "nothing to show for this post at all".
  // The counts query groups by post, so a post with nothing in the window comes
  // back with no row: once it has settled, a missing row means zero, not
  // unknown. Reading it as unknown leaves the empty state unreachable.
  const { counts: postCounts, loading: postCountsLoading } =
    useCommentCountsForPost(postId ?? '')
  const postTotalCommentCount =
    !postId || postCountsLoading
      ? undefined
      : postCounts
        ? postCounts.unreplied + postCounts.replied + postCounts.dismissed
        : 0

  // Placed below the empty-state early return, so neither notice claims a
  // window over a list that shows nothing. Under oldest-first the oldest
  // comments lead the list, so the hint leads it too.
  const isByPostView = useIsByPostView()
  const noticeLeadsList = !isByPostView && sortOption === 'oldest'
  const isPostOlderThanRetentionWindow = isOlderThanRetentionWindow(post?.dueAt)
  const retentionNotice = isByPostView ? (
    isPostOlderThanRetentionWindow ? (
      <RetentionNotice
        externalLink={post?.externalLink}
        service={post?.channelService}
      />
    ) : undefined
  ) : isRetentionWindowActive(publishedAt) ? (
    <RetentionNotice
      className={noticeLeadsList ? styles.leadingRetentionNotice : undefined}
    />
  ) : undefined

  // Determine which empty state to show (if any)
  const { EmptyState, showEmptyStateWithComments } = useCommentsEmptyState({
    channelIds,
    hasComments: !!query.data && comments.hasComments,
    hasCommentsInList: comments.hasComments,
    hasActiveFilters,
    status,
    onClearFilters: clearFilters,
    onNavigateToUnreplied,
    isLastVisibleComment: comments.list.length === 1,
    allCommentsLocallyResolved: coordination.allLocallyResolved,
    hasLocallyUnresolvedComments: coordination.hasLocallyUnresolved,
    channels,
    postId,
    postTotalCommentCount,
    isPostOlderThanRetentionWindow,
    singleChannelId,
    isSingleChannelRealtime,
  })

  // Only skeleton when there's nothing to show. Under `cache-and-network`,
  // `loading` stays true during a background refetch (e.g. one triggered when
  // opening a comment thread writes partial data to the cache) — blanking the
  // whole feed then would unmount every card and drop their local state, so
  // keep the cached list visible. `isLoading` (dismiss-all) still forces it.
  if ((loading && !fetchingMore && !comments.hasComments) || isLoading)
    return <CommentListSkeleton />

  if (error)
    return <CommentsError errorMessage={error.message} onRetry={retry} />

  const shouldCheckForOldComments = isMonthlyCleanupStatus(filters.status)

  // Show only the empty state when there are no comments (and it's not the
  // celebratory caught-up state shown above a still-visible list).
  if (EmptyState && !showEmptyStateWithComments) {
    return <CommentsEmptyStateView emptyState={EmptyState} />
  }

  return (
    <FlexMotion ref={containerRef} direction="column" gap="md" fullWidth>
      <CommentsListNotices
        singleChannel={singleChannel}
        channels={channels}
        shouldCheckForOldComments={shouldCheckForOldComments}
        onBannerDismissed={onBannerDismissed}
        showEmptyStateWithComments={showEmptyStateWithComments}
        emptyState={EmptyState}
      />
      {noticeLeadsList && retentionNotice}
      <m.ol
        aria-busy={fetchingMore}
        className={styles.feed}
        tabIndex={0}
        aria-label="Comments list"
      >
        {comments.list.map((comment, index) => (
          <React.Fragment key={comment.id}>
            {index === dividerIndex && (
              <NewItemsDivider
                label="New comments"
                arrowDown={direction === 'bottom'}
              />
            )}
            <CommentListItem
              comment={comment}
              channel={channelById.get(comment.channelId)}
              isLastItem={index === comments.list.length - 1}
              lastElementRef={lastElementRef}
              onExpandNextComment={coordination.expandNext}
              onExpandPreviousComment={coordination.expandPrevious}
              onManualExpansion={coordination.expand}
              onLocalResolutionChange={coordination.setResolution}
              isNew={newlyArrivedIds.has(comment.id)}
            />
          </React.Fragment>
        ))}
        {fetchingMore && (
          <li className={styles.loading}>
            <SimpleSpinner size="small" />
          </li>
        )}
      </m.ol>
      {!noticeLeadsList && retentionNotice}
    </FlexMotion>
  )
}

export const CommentListSkeleton = (): JSX.Element => (
  <Flex direction="column" gap="md" fullWidth>
    {/* Keep the label a sibling of the <ol>; a list should only contain <li>. */}
    <VisuallyHidden>Loading comments</VisuallyHidden>
    <CommentFeed aria-busy="true" aria-label="Comments list">
      {range(20).map((index) => (
        <CommentCardSkeleton key={index} />
      ))}
    </CommentFeed>
  </Flex>
)

export const CommentFeed = ({
  children,
  ...props
}: HTMLAttributes<HTMLOListElement>): JSX.Element => (
  <ol {...props} className={styles.feed}>
    {children}
  </ol>
)
